/**
 * useWorkflowPermissions — Hook to check workflow permissions for UI gating.
 *
 * sl-za0v.10.3: Fetches the current user's permissions on a specific workflow
 * from the SWR API. Used by Automation Sidebar, Workflow Builder, and History Panel
 * to show/hide/disable UI elements based on RBAC.
 *
 * Returns loading state + individual permission booleans.
 */
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { defaultWorkspaceApiClient } from '../../cloud';

// =============================================================================
// Types
// =============================================================================

export interface WorkflowPermissions {
  canEdit: boolean;
  canRun: boolean;
  canViewResults: boolean;
  canDelete: boolean;
  canPublish: boolean;
  canManagePermissions: boolean;
}

const NO_PERMISSIONS: WorkflowPermissions = {
  canEdit: false,
  canRun: false,
  canViewResults: false,
  canDelete: false,
  canPublish: false,
  canManagePermissions: false,
};

const ALL_PERMISSIONS: WorkflowPermissions = {
  canEdit: true,
  canRun: true,
  canViewResults: true,
  canDelete: true,
  canPublish: true,
  canManagePermissions: true,
};

// =============================================================================
// Hook
// =============================================================================

export function useWorkflowPermissions(workflowId: string | null): {
  permissions: WorkflowPermissions;
  loading: boolean;
  refresh: () => void;
} {
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const [permissions, setPermissions] =
    useState<WorkflowPermissions>(NO_PERMISSIONS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!workflowId) {
      // No workflow selected yet — allow all actions so the user can select/create one.
      // Backend enforces actual permissions once a workflow is chosen.
      setPermissions(ALL_PERMISSIONS);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const path = `/api/workspace/${workspaceId}/workflow/${workflowId}/my-permissions`;
      const res = await defaultWorkspaceApiClient.request({
        workspaceId,
        path,
        category: 'permissions',
        key: `GET:${path}`,
        mode: 'background',
        priority: 'visible',
      });

      if (!res.ok) {
        // Graceful degradation — backend enforces permissions, frontend just gates UI
        setPermissions(ALL_PERMISSIONS);
        return;
      }

      const data: { permissions: string[] } = await res.json();
      const perms = new Set(data.permissions);

      setPermissions({
        canEdit: perms.has('workflow.edit'),
        canRun: perms.has('workflow.run'),
        canViewResults: perms.has('workflow.view_results'),
        canDelete: perms.has('workflow.delete'),
        canPublish: perms.has('workflow.publish'),
        canManagePermissions: perms.has('workflow.manage_permissions'),
      });
    } catch {
      // On network error, assume all permissions (graceful degradation — backend enforces)
      setPermissions(ALL_PERMISSIONS);
    } finally {
      setLoading(false);
    }
  }, [workflowId, workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { permissions, loading, refresh: () => void refresh() };
}
